/**
 * Burn-out, crise de santé mentale et les 4 game over — SPEC-MOTEUR §11.
 *
 * Chaque fin est **télégraphiée** (PLAN §4.11) : le jeu avertit avant de tuer. C'est la
 * règle de design qui structure ce module — aucune condition ne se déclenche sans qu'un
 * `EngineEvent` de kind `warning` soit passé avant.
 *
 * **Avenant A-1** : la faillite n'existe pas avant le Grand Saut (palier 1 000) — sans
 * couche argent, pas d'argent négatif.
 */
import { H_CRISE, H_DEMI_VIE_EVERGREEN, H_FAILLITE, H_JOUR, H_SEMAINE, H_STRIKE, } from "./clock.js";
import { SYSTEMES_TOUS_ACTIFS } from "./menage.js";
import { appliquerTraitJauge } from "./traits.js";
/**
 * Zone de SM (§11.1). Les effets sont appliqués ailleurs, là où ils comptent :
 * Qualité ×0,9 en Fatigue dans `formula.ts`, poids des events négatifs ×2 en zone rouge
 * dans `events.ts`, désaturation et tremblement côté UI (SPEC-UI §7.5).
 */
export function zoneSM(mental) {
    if (mental <= 0)
        return "crise";
    if (mental < 30)
        return "rouge";
    if (mental < 60)
        return "fatigue";
    return "vert";
}
/** Sortie de crise (§11.1) : SM 40, énergie 100, momentum 0,8. */
export const CRISE_SM_SORTIE = 40;
export const CRISE_MOMENTUM_SORTIE = 0.8;
/** Abonnés perdus par jour de crise, composés sur les 14 jours (§11.1). */
export const CRISE_PERTE_PAR_JOUR = 0.005;
/** Fenêtre au-delà de laquelle une 2e crise ne tue plus (§11.2) : 90 jours. */
export const BURNOUT_FENETRE_H = 90 * H_JOUR;
/**
 * Crise de SM = 0 (§11.1) — arrêt forcé de **14 jours simulés**, résolu immédiatement en un
 * bloc. MUTE `state`.
 *
 * `renteAbonnes` est la rente evergreen versée pendant la période, calculée par l'appelant
 * (`sleep.ts` détient la formule §6.3) : c'est **l'assurance burn-out** du PLAN §4.10, elle
 * est versée normalement et **déduite de la perte**. La passer à 0 revient à dire « aucune
 * evergreen active », ce qui est le cas d'une chaîne jeune.
 */
export function resoudreCrise(state, renteAbonnes = 0, events = []) {
    const avant = state.subscribers;
    // Lu AVANT tout : c'est l'état du drapeau à l'entrée de cette crise qui dit si un remède
    // a été pris depuis la précédente. Le relire après le réarmement ci-dessous rendrait
    // n'importe quelle 2e crise rétroactivement terminale.
    const avertissementActifAvant = state.flags.dernier_avertissement_burnout;
    // −0,5 % par jour, composés sur 14 jours.
    const facteur = Math.pow(1 - CRISE_PERTE_PAR_JOUR, H_CRISE / H_JOUR);
    const apresPerte = avant * facteur;
    const perdus = Math.round(avant - apresPerte);
    const sauves = Math.min(perdus, Math.round(renteAbonnes));
    state.subscribers = Math.max(0, avant - perdus + sauves);
    // Les volatils partent en premier (§4.7).
    state.subs_volatile = Math.max(0, Math.min(state.subs_volatile, state.subscribers));
    state.clock_h += H_CRISE;
    state.mental = CRISE_SM_SORTIE;
    state.energy = 100;
    state.momentum = CRISE_MOMENTUM_SORTIE;
    state.counters.sm_crisis_history_h.push(state.clock_h);
    events.push({
        kind: "crisis_started",
        data: { abonnes_perdus: perdus - sauves, jours: H_CRISE / H_JOUR, rente_deduite: sauves },
    });
    // La 2e crise dans les 90 jours, sans remède entre les deux, est terminale (§11.2).
    const terminal = criseEstTerminale(state, avertissementActifAvant);
    if (terminal) {
        // La terminalité se décide À CET INSTANT, avec l'historique sous les yeux : on l'inscrit
        // tout de suite plutôt que de la laisser re-dériver d'un drapeau devenu ambigu.
        appliquerGameOver(state, "burnout_terminal", events);
    }
    else {
        // Après la 1re crise : dernier avertissement + écran dédié (§11.1).
        if (!state.flags.dernier_avertissement_burnout) {
            state.flags.dernier_avertissement_burnout = true;
            events.push({
                kind: "warning",
                data: { cause: "dernier_avertissement_burnout", fenetre_h: BURNOUT_FENETRE_H },
            });
        }
        events.push({
            kind: "crisis_ended",
            data: { mental: state.mental, momentum: state.momentum },
        });
    }
    return {
        abonnes_perdus: perdus - sauves,
        abonnes_sauves_par_la_rente: sauves,
        heures_perdues: H_CRISE,
        game_over: terminal,
    };
}
/**
 * §11.2 (1) — la crise courante est-elle terminale ? Deux crises séparées de moins de
 * `BURNOUT_FENETRE_H` = 90 jours = **2 160 h** (⟦A-19⟧, pas 1 440 h) **et** aucun remède
 * entre les deux.
 *
 * `avertissementActifAvant` est l'état de `flags.dernier_avertissement_burnout` **à l'entrée
 * de la crise courante** : `pause` et `therapie` l'effacent, c'est lui qui porte « un remède
 * a été pris depuis la crise précédente ». Ce paramètre est explicite **exprès** : le drapeau
 * est réarmé dès qu'une crise est survécue, donc le relire dans l'état après coup ferait
 * mentir la fonction.
 */
export function criseEstTerminale(state, avertissementActifAvant) {
    const histo = state.counters.sm_crisis_history_h;
    if (histo.length < 2)
        return false;
    const derniere = histo[histo.length - 1];
    const precedente = histo[histo.length - 2];
    if (derniere - precedente >= BURNOUT_FENETRE_H)
        return false;
    return avertissementActifAvant;
}
/** `pause` et `therapie` sont les remèdes : ils lèvent le dernier avertissement (§11.2). */
export function appliquerRemedeSM(state) {
    state.flags.dernier_avertissement_burnout = false;
}
/**
 * ⟦PLAN §4.10 · A-74⟧ **« SMS de ta mère »** — la touche humaine de la zone rouge, jamais
 * écrite jusqu'ici. PLAN §4.10 la liste avec l'écran désaturé et les probas négatives ×2 :
 * quand la SM passe sous 30, quelqu'un dehors s'en aperçoit. C'est du télégraphiage (PLAN
 * §4.11, « le jeu avertit avant de tuer ») sous sa forme la plus douce : pas une sirène, un
 * message qui demande si tu manges le dimanche.
 *
 * **Un SMS par épisode, pas par tour.** Le témoin `flags.sms_mere_envoye` se pose au premier
 * tour en zone rouge et ne se réarme qu'au retour en zone VERTE (≥ 60) : l'hystérésis évite
 * qu'une SM qui oscille autour du seuil fasse sonner le téléphone toutes les nuits — même
 * logique que les alertes de faillite, qui ne tombent qu'au jalon franchi.
 *
 * **État neutre ⟦A-10⟧ par construction** : aucun `draw`, aucune jauge touchée, un
 * `EngineEvent` et un drapeau que rien d'autre ne lit. Une campagne avant/après rend le même
 * md5 — c'est mesuré dans A-74.
 *
 * MUTE `state` (le drapeau témoin). Rend `true` si le SMS vient de partir.
 */
export function smsDeTaMere(state, events = []) {
    if (state.status !== "active")
        return false;
    const zone = zoneSM(state.mental);
    // Retour au vert : l'épisode est clos, le prochain passage en rouge vaudra un nouveau SMS.
    if (zone === "vert") {
        if (state.flags.sms_mere_envoye)
            state.flags.sms_mere_envoye = false;
        return false;
    }
    if (zone !== "rouge" || state.flags.sms_mere_envoye)
        return false;
    state.flags.sms_mere_envoye = true;
    events.push({
        kind: "warning",
        data: { cause: "sms_de_ta_mere", mental: state.mental },
    });
    return true;
}
// ─────────────────────────────────────────────────────────────────────────────
// §11.2 (2) Spirale de réputation — l'arc de la chute
// ─────────────────────────────────────────────────────────────────────────────
/** L'arc de la chute s'ouvre à REP ≤ 10 et retombe au-dessus de REP 25 (§11.2). */
export const ARC_OUVERTURE_REP = 10;
export const ARC_FERMETURE_REP = 25;
/**
 * « Laisser retomber » (§11.2) — délai minimal entre le bad buzz et la **publication** des
 * excuses pour qu'elles soient réussies.
 *
 * ⚠️ **Ce seuil valait `2 * H_JOUR` (48 h), et il ne pouvait alors JAMAIS mordre.**
 * ⟦audit des quatre maillons, 19/08, rupture 4⟧ Le seul chemin de production est la vidéo
 * jalon, qui coûte `H_VIDEO_JALON = 4 * H_JOUR` = 96 h (`actions.ts`), et `applyAction` avance
 * l'horloge au pas 4 **avant** l'effet du pas 6 qui appelle `resoudreExcuses`. Le délai mesuré
 * valait donc **toujours ≥ 96 h ≥ 48 h** : la branche `aLaisseRetomber === false` était morte,
 * et PLAN §4.11 promettait pourtant « skill de crise **+ timing laisser retomber** ». Le joueur
 * qui se précipitait réussissait exactement comme celui qui attendait. La leçon signature de la
 * mécanique — *attends que ça retombe* — n'était ni enseignée ni sanctionnée.
 *
 * ## 🔴 Pourquoi la valeur n'a PAS été relevée : régler ce nombre ne peut rien régler
 *
 * Le délai mesuré vaut `temps_d_attente + heures_de_production`, avec `temps_d_attente ≥ 0` et
 * `heures_de_production = H_VIDEO_JALON` (96 h ; ~75 h au meilleur cumul d'`action_cost_mult`).
 * Le joueur qui **se précipite** publie donc toujours à exactement 96 h. D'où une alternative
 * fermée, sans milieu :
 *
 * | seuil | ce qui se passe |
 * |---|---|
 * | ≤ 96 h | la branche « à chaud » est **morte** — l'état d'aujourd'hui |
 * | > 96 h | le joueur pressé **rate**, et pendant l'arc de la chute il **meurt** |
 *
 * Il n'existe aucune valeur qui morde sans tuer : le réglage du nombre est un faux levier.
 *
 * ## Ce que coûterait de le relever — mesuré, pas supposé
 *
 * Essai à `6 * H_JOUR` (144 h), campagne `--runs 4` (100 parties), mêmes graines, worktree
 * épinglé sur HEAD :
 *
 * | | avant | après |
 * |---|---|---|
 * | **survie prudents** ⟦A-40, bloquant ≥ 0,95⟧ | **1,0000** | **0,9167** 🔴 |
 * | survie négligents ⟦bloquant < 0,20⟧ | 0,0000 | 0,0000 |
 * | ⟦A-22⟧ exécution forte | 16/16 | 13/16 |
 * | victoires 1M | 20/100 | 15/100 |
 * | game over `spirale_reputation` | 0 | 5 |
 * | vidéos 1 k → 1 M | 284 | 283 |
 *
 * **Une cible bloquante tombe.** Les cinq morts sont toutes des joueurs qui tournent leurs
 * excuses dès le bad buzz, pendant l'arc — et rien à l'écran ne leur dit d'attendre : le
 * panneau Jalons annonce « Les conditions sont réunies. Il te faut 60 de moyenne pour la
 * réussir. », jamais le délai. Faire mordre le seuil sans l'afficher, ce serait un game over
 * **non télégraphié**, exactement ce que PLAN §4.11 interdit et ce que cette même session
 * vient de réparer sur le Fantôme.
 *
 * ## ✎ L'arbitrage revient à Mathieu — et il porte sur la SANCTION, pas sur le nombre
 *
 *  1. **Bloquer plutôt que tuer** (recommandé) — le délai rejoint `conditionsManquantes`
 *     (`jalons.ts`) : tant que le buzz est chaud, le jalon n'est pas `jouable`, l'écran écrit
 *     « il faut laisser retomber : encore N jours », le bouton est éteint. Le timing devient
 *     réel et enseigné, la survie ne bouge pas (la politique de `simulate.ts` lit `jouable` du
 *     moteur), et la branche « à chaud » reste la garde de dernier recours pour un appel API
 *     direct. Coût : une condition dans `jalons.ts`, une ligne dans `Jalons.tsx`.
 *  2. **Dégrader plutôt que tuer** — se précipiter réussit mais ne restitue que ~40 % au lieu
 *     de 80 %. La leçon passe par le portefeuille. ⚠️ s'écarte de PLAN §4.11, qui écrit
 *     « ratée (sans skill, **à chaud**) c'est le ukulélé ».
 *  3. **Assumer les 5 morts** — alors c'est la cible ⟦A-40⟧ qu'il faut bouger, et elle
 *     appartient à Mathieu.
 *
 * En attendant, la valeur reste celle d'⟦A-19⟧ : **rien ne bouge, l'état neutre est préservé**,
 * et le test de `gameover.test.ts` rend la branche morte VISIBLE au lieu de la laisser dormir.
 *
 * ✎ SPEC-MOTEUR §11.2 écrit « ≥ 32 h (2 jours) ✎ » : 32 h datait de la journée à 16 h, et le
 * ✎ dit que la valeur est un réglage. ⟦A-19⟧ l'a converti en jours sans voir qu'aucune valeur
 * sous 96 h ne pouvait rien changer.
 */
export const EXCUSES_DELAI_H = 2 * H_JOUR;
/** Part des dégâts restituée par des excuses réussies (PLAN §4.11). */
export const EXCUSES_RESTITUTION = 0.8;
/** Met l'arc de la chute à jour selon la réputation (§11.2). MUTE `state`. */
export function majArcDeLaChute(state, events = [], sys = SYSTEMES_TOUS_ACTIFS) {
    // ⟦C60 · A-177⟧ Le bad buzz n'est plus une SANCTION : l'arc de la chute (et la spirale de
    // réputation qui en sortait) ne s'ouvre plus, et un arc resté ouvert dans une run en cours se
    // referme. Actif (le défaut), la règle d'avant, au caractère près.
    if (!sys.bad_buzz) {
        if (state.flags.arc_chute_active)
            state.flags.arc_chute_active = false;
        return;
    }
    if (!state.flags.arc_chute_active && state.reputation <= ARC_OUVERTURE_REP) {
        state.flags.arc_chute_active = true;
        events.push({ kind: "warning", data: { cause: "arc_chute_ouvert", reputation: state.reputation } });
        return;
    }
    if (state.flags.arc_chute_active && state.reputation > ARC_FERMETURE_REP) {
        state.flags.arc_chute_active = false;
        events.push({ kind: "event_resolved", data: { cause: "arc_chute_ferme", reputation: state.reputation } });
    }
}
/**
 * La vidéo d'excuses (§11.2) — c'est un choix d'événement.
 *
 * **Réussie** si « Gestion de crise » est possédé **ET** publiée ≥ `EXCUSES_DELAI_H` après le
 * bad buzz déclencheur (« laisser retomber ») : restitue **80 %** des `last_damage`, arrondi au
 * plus proche, et referme l'arc. **Ratée** sinon (« à chaud », ou sans le skill) : et si l'arc
 * de la chute est actif, c'est la fin immédiate.
 *
 * ⚠️ **Les deux causes d'échec sont distinctes et le `warning` les distingue** — `a_le_skill`
 * et `delai_h` / `delai_requis_h`. C'est ce qui permet au journal de dire *pourquoi* les
 * excuses sont tombées à plat : « tu ne sais pas encore gérer une crise » et « tu as filmé à
 * chaud » demandent au joueur deux gestes différents, et un texte unique en aurait rendu un
 * faux sur deux. Même leçon que la traduction des deux voies du Fantôme.
 *
 * MUTE `state`.
 */
export function resoudreExcuses(state, aGestionDeCrise, events = []) {
    const degats = state.last_damage;
    const delai = degats === null ? Number.POSITIVE_INFINITY : state.clock_h - degats.at_h;
    const aLaisseRetomber = delai >= EXCUSES_DELAI_H;
    const reussies = aGestionDeCrise && aLaisseRetomber;
    if (!reussies) {
        events.push({
            kind: "warning",
            data: {
                cause: "excuses_ratees",
                a_le_skill: aGestionDeCrise,
                delai_h: Number.isFinite(delai) ? delai : null,
                // ⟦rupture 4⟧ Le seuil voyage avec le fait : sans lui, l'écran devrait le redériver
                // — et c'est exactement comme ça que deux fichiers finissent par ne plus être d'accord.
                delai_requis_h: EXCUSES_DELAI_H,
                a_laisse_retomber: aLaisseRetomber,
                arc_actif: state.flags.arc_chute_active,
            },
        });
        // Ukulélé : meme permanent, l'aggravation est définie par l'événement de contenu.
        return {
            reussies: false,
            abonnes_rendus: 0,
            reputation_rendue: 0,
            game_over: state.flags.arc_chute_active,
        };
    }
    const abonnes = Math.round((degats?.subs_lost ?? 0) * EXCUSES_RESTITUTION);
    // ⟦A-4 §2 / A-26⟧ La restitution de Réputation est le SEUL gain de Réputation du moteur :
    // sans ce passage par le trait, « le préjugé favorable » du créateur ne mordait nulle part.
    const reputation = Math.round(appliquerTraitJauge(state.genre, "reputation", (degats?.rep_lost ?? 0) * EXCUSES_RESTITUTION));
    state.subscribers += abonnes;
    state.reputation = Math.min(100, state.reputation + reputation);
    state.flags.arc_chute_active = false;
    state.last_damage = null;
    events.push({
        kind: "event_resolved",
        data: { cause: "excuses_reussies", abonnes_rendus: abonnes, reputation_rendue: reputation },
    });
    return { reussies: true, abonnes_rendus: abonnes, reputation_rendue: reputation, game_over: false };
}
// ─────────────────────────────────────────────────────────────────────────────
// §11.4 Bannissement — les strikes
// ─────────────────────────────────────────────────────────────────────────────
/** Nombre de strikes actifs simultanés qui bannit la chaîne (§11.4). */
export const STRIKES_FATAL = 3;
/**
 * `counters.strikes` porte les clock_h d'**expiration** : un strike vit `H_STRIKE`, soit
 * 90 jours = **2 160 h** (§2.1).
 *
 * ⚠️ Ce commentaire disait « 1 440 h » : faux depuis ⟦A-19⟧, qui a fait passer la journée
 * simulée de 16 h à 24 h. Les durées de règle s'écrivent `× H_JOUR`, jamais en heures
 * absolues (SPEC-MOTEUR §2.1) — précisément pour que ce genre d'écart ne survive pas.
 */
export function strikesActifs(state) {
    return state.counters.strikes.filter((expire) => expire > state.clock_h);
}
/** Retire les strikes expirés (§2.4 pas 6). MUTE `state`. */
export function purgerStrikes(state) {
    const avant = state.counters.strikes.length;
    state.counters.strikes = strikesActifs(state);
    return avant - state.counters.strikes.length;
}
/**
 * Pose un strike, qui expirera `H_STRIKE` = **2 160 h** plus tard (⟦A-19⟧, pas 1 440 h).
 * MUTE `state`.
 *
 * ⚠️ **Cette fonction n'a AUCUN appelant en production** (seuls des tests l'appellent) : les
 * strikes du jeu arrivent tous par l'effet de contenu `strike_delta` (`effects.ts`), qui
 * pousse l'expiration directement et n'émet qu'un `LogEntry`. Conséquence : l'`EngineEvent`
 * `kind: "warning"` / `cause: "strike"` ci-dessous **n'est jamais émis**, et le `case
 * "strike"` du journal client (`client/src/game/journal.ts`) ne se déclenche jamais. C'est
 * le canal de télégraphiage d'un strike REÇU, et il est mort — voir ⟦A-94 §4⟧ route 2.
 */
export function poserStrike(state, events = []) {
    state.counters.strikes.push(state.clock_h + H_STRIKE);
    const actifs = strikesActifs(state).length;
    events.push({
        kind: "warning",
        data: { cause: "strike", actifs, fatal_a: STRIKES_FATAL, expire_h: state.clock_h + H_STRIKE },
    });
}
// ─────────────────────────────────────────────────────────────────────────────
// §11.5 Faillite
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Jalons d'alerte de la descente, en heures écoulées depuis l'entrée en négatif (§11.5) :
 * J−30 (l'entrée), J−15, J−7.
 *
 * ⚠️ ⟦A-19⟧ Écrits `[0, 240, 368]` en dur jusqu'ici — 240 h valaient 15 jours et 368 h en
 * valaient 23 **quand la journée en faisait 16**. Laissés absolus, ils seraient tombés à
 * J−20 et J−14,7 d'une descente qui en dure 30 : le télégraphiage aurait menti au joueur
 * sans que rien ne le signale. Ils se dérivent donc de `H_JOUR`, comme `H_FAILLITE`.
 */
export const FAILLITE_ALERTES_H = [0, 15 * H_JOUR, 23 * H_JOUR];
/**
 * Met à jour la descente vers la faillite et émet ses alertes (§11.5). MUTE `state`.
 *
 * **Avenant A-1** : rien ne se passe avant le Grand Saut — sans couche argent, la condition
 * ne s'évalue pas. `clockAvant` est l'horloge d'avant le pas de temps courant : elle permet
 * de savoir quels jalons d'alerte ont été franchis **pendant** ce pas, sans stocker de
 * drapeau par alerte.
 */
export function majFaillite(state, clockAvant, events = [], sys = SYSTEMES_TOUS_ACTIFS) {
    // ⟦C60 · A-177⟧ Plus de faillite : la descente ne démarre jamais (et une descente déjà
    // ouverte dans une run en cours est effacée). Actif (le défaut), la règle d'avant.
    if (!sys.faillite) {
        state.counters.money_negative_since_h = null;
        return;
    }
    if (!state.flags.grand_saut_done) {
        state.counters.money_negative_since_h = null;
        return;
    }
    if (state.money >= 0) {
        if (state.counters.money_negative_since_h !== null) {
            state.counters.money_negative_since_h = null;
            // Sortir du rouge réarme le sponsor : « une fois par descente » (§11.5).
            state.flags.sponsor_derniere_chance_used = false;
            events.push({ kind: "event_resolved", data: { cause: "retour_dans_le_vert", money: state.money } });
        }
        return;
    }
    // Entrer dans le rouge maintenant, ou y être déjà : la distinction fait toute la logique
    // des alertes, sinon l'alerte d'entrée se réémet à chaque appel.
    const entreeMaintenant = state.counters.money_negative_since_h === null;
    if (entreeMaintenant)
        state.counters.money_negative_since_h = state.clock_h;
    const debut = state.counters.money_negative_since_h;
    // `-1` à l'entrée : aucun jalon n'a encore été franchi, y compris le jalon 0.
    const ecouleAvant = entreeMaintenant ? -1 : Math.max(0, clockAvant - debut);
    const ecouleApres = state.clock_h - debut;
    for (const jalon of FAILLITE_ALERTES_H) {
        // Franchi pendant CE pas de temps uniquement : une alerte ne tombe jamais deux fois.
        if (!(ecouleApres >= jalon && ecouleAvant < jalon))
            continue;
        events.push({
            kind: "warning",
            data: {
                cause: "faillite_imminente",
                jours_restants: (H_FAILLITE - jalon) / H_JOUR,
                money: state.money,
            },
        });
    }
}
/** Le sponsor « dernière chance » est-il dû ? Une fois par descente, au sommeil (§11.5). */
export function sponsorDerniereChanceDu(state, sys = SYSTEMES_TOUS_ACTIFS) {
    // ⟦C60 · A-177⟧ Le sponsor « dernière chance » est le filet de la faillite : sans faillite, il
    // n'a plus rien à rattraper.
    if (!sys.faillite)
        return false;
    return (state.flags.grand_saut_done &&
        state.money < 0 &&
        state.counters.money_negative_since_h !== null &&
        !state.flags.sponsor_derniere_chance_used);
}
// ─────────────────────────────────────────────────────────────────────────────
// §9.4bis « Le Fantôme » — la 5e fin, par extinction ⟦A-4 décision 1 + arbitrage F⟧
// ─────────────────────────────────────────────────────────────────────────────
/**
 * La 5e fin. Les quatre autres sont des **accidents** — on se brûle, on dérape, on se fait
 * bannir, on fait faillite. Le Fantôme est le seul qui n'en soit pas un : c'est l'extinction
 * lente, la chaîne qu'on n'arrête pas mais qui s'éteint.
 *
 * ⟦A-74⟧ **La valeur vit désormais dans `GameOverId`** (`types.ts`), comme le repli d'A-58 le
 * prévoyait mot pour mot (« ajouter `| "fantome"` et supprimer les deux `as` ») : le fichier
 * n'appartient plus à une session concurrente, la dette est soldée. Les deux conversions de
 * type ont disparu avec elle.
 */
export const FANTOME = "fantome";
/** **Extinction** : vidéos nulles consécutives exigées ⟦A-4 F⟧. */
export const FANTOME_FLOPS = 5;
/** **Extinction** : momentum au plancher — la même valeur que la sortie de crise (§11.1). */
export const FANTOME_MOMENTUM_PLANCHER = CRISE_MOMENTUM_SORTIE;
/**
 * **Extinction** : 60 **jours simulés** de rente hebdomadaire nulle ⟦A-4 F⟧.
 *
 * ⚠️ SPEC-MOTEUR §9.4bis écrit « 60 jours simulés (960 h) » et §9.4bis « 90 jours (1 440 h) » :
 * ces heures datent de la journée à 16 h. `H_JOUR` en vaut **24** depuis ⟦A-19⟧, qui avait déjà
 * corrigé le même piège sur les alertes de faillite (`FAILLITE_ALERTES_H`). Les **jours** sont
 * la donnée normative, les heures s'en dérivent — sinon le Fantôme tomberait à J40 et J60.
 */
export const FANTOME_RENTE_NULLE_H = 60 * H_JOUR; // 1 440 h
/**
 * **Jamais décollé** : jours simulés sans publication, hors pause annoncée.
 *
 * ⟦A-79⟧ **21 jours ✎, plus 90.** Mathieu recadre la voie : *« la mort fantôme elle doit
 * arriver tôt, c'est juste pour dire à la personne si tu commences pas à faire des vidéos,
 * c'est game over direct »*. Les 90 jours d'⟦A-4 F⟧ en faisaient une agonie de trimestre —
 * l'inverse d'une leçon de début de partie. Trois semaines : assez tôt pour être la leçon,
 * assez large pour qu'aucun profil légitime à démarrage lent ne se fasse faucher (le harnais
 * publie sa première vidéo médiane à J0-J1 ; la crise de SM, l'autre grand silence forcé,
 * dure 14 jours et laisse une semaine pour publier). La pause annoncée ⟦A-2⟧ protège
 * toujours : son gel ne compte pas comme du silence (`heuresDeSilence`).
 */
export const FANTOME_SILENCE_H = 21 * H_JOUR; // 504 h
/**
 * Sous **une vue par semaine**, la rente evergreen est nulle : c'est la granularité en dessous
 * de laquelle la chaîne ne touche littéralement plus rien. Le decay §6.3 n'atteint jamais zéro
 * exactement — il fallait un seuil, et il fallait qu'il soit dans l'unité du joueur.
 */
export const FANTOME_RENTE_SEUIL_VUES = 1;
/** Le télégraphiage s'ouvre à mi-chemin (§9.4bis, « en deux temps »). */
export const FANTOME_MI_CHEMIN = 0.5;
/**
 * Depuis combien d'heures la rente hebdomadaire est-elle **nulle** ? Lecture pure de l'état,
 * **sans compteur ajouté** — et ce n'est pas de la coquetterie : `Counters` vit dans
 * `types.ts`, et un champ de plus y serait un état à migrer pour une information qui est déjà
 * dans les vidéos.
 *
 * La rente d'une evergreen vaut `vues_pic × weekly_rate × 0,5^(âge/T½)` (§6.3) : elle est
 * **strictement décroissante**, donc l'instant où elle passe sous le seuil se résout en forme
 * fermée — `anchor_h + T½ × log₂(pic / seuil)`. La rente de la chaîne est nulle depuis le
 * **dernier** de ces instants, toutes vidéos confondues.
 *
 * ✎ **Les vidéos passées en privé comptent quand même.** Elles ne rapportent plus (§6.5) mais
 * on ignore *depuis quand*, et se tromper ici avancerait la mort du joueur. Entre retarder la
 * fin et la précipiter, ce module retarde.
 */
export function heuresDeRenteNulle(state) {
    let depuis = 0; // aucune evergreen n'a jamais payé : nulle depuis le début de la run
    for (const video of state.videos) {
        if (!video.evergreen)
            continue;
        const pic = video.vues_pic * video.evergreen.weekly_rate;
        const instant = pic <= FANTOME_RENTE_SEUIL_VUES
            ? video.evergreen.anchor_h
            : video.evergreen.anchor_h +
                H_DEMI_VIE_EVERGREEN * Math.log2(pic / FANTOME_RENTE_SEUIL_VUES);
        if (instant > depuis)
            depuis = instant;
    }
    return Math.max(0, state.clock_h - depuis);
}
/**
 * Les deux voies et leur avancement (§9.4bis). **Ne mute rien.**
 *
 * `silenceH` vient de `heuresDeSilence` (`sleep.ts`), qui sait déjà retirer le gel d'une pause
 * annoncée protégée ⟦A-2⟧ — « le gel de A-2 ne compte pas comme du silence ». Le paramètre est
 * explicite **exprès** : l'importer ici fermerait le cycle `gameover → sleep → actions →
 * jalons → gameover`, et un cycle d'initialisation pour une seule lecture est un mauvais
 * marché. Même idiome que `criseEstTerminale(state, avertissementActifAvant)`.
 */
export function voiesDuFantome(state, silenceH) {
    // Les deux gardes instantanées : la rente est l'horloge, les flops et le momentum disent que
    // la chaîne est bien en train de s'éteindre et pas simplement jeune.
    //
    // ⚡ **L'horloge n'est calculée que si les gardes sont armées**, et ce n'est pas une
    // micro-optimisation gratuite : `extinctionTelegraphiee` est appelée par `poidsModule` pour
    // **chaque événement candidat de chaque tirage**. Balayer les centaines de vidéos d'une run
    // longue à chaque candidat coûterait plus cher que tout le reste du tirage réuni.
    const armee = state.counters.consecutive_flops >= FANTOME_FLOPS &&
        state.momentum <= FANTOME_MOMENTUM_PLANCHER;
    return [
        {
            voie: "extinction",
            armee,
            heures: armee ? heuresDeRenteNulle(state) : 0,
            seuil: FANTOME_RENTE_NULLE_H,
        },
        {
            voie: "jamais_decolle",
            armee: true,
            heures: Math.max(0, silenceH),
            seuil: FANTOME_SILENCE_H,
        },
    ];
}
/**
 * La chaîne est-elle dans la **zone télégraphiée** (au-delà de la mi-chemin d'une voie armée) ?
 *
 * C'est la lecture qu'`events.ts` utilise pour faire cesser les événements positifs (§9.4bis,
 * « le Fil se vide et les événements positifs cessent ») et que l'UI peut afficher. **Fausse
 * par défaut** : une chaîne qui publie n'y entre jamais — l'état neutre d'⟦A-10⟧.
 *
 * ✎ `silenceH` vaut **0 par défaut**, ce qui neutralise la voie « jamais décollé » et ne
 * regarde donc que l'extinction. C'est exactement ce dont `events.ts` a besoin — il n'a pas
 * `heuresDeSilence` sous la main — et c'est sans conséquence : une chaîne qui ne publie plus
 * depuis 45 jours ne tire presque plus d'événements de publication de toute façon.
 */
export function extinctionTelegraphiee(state, silenceH = 0) {
    if (state.victory_locked || state.status !== "active")
        return false;
    return voiesDuFantome(state, silenceH).some((v) => v.armee && v.heures >= v.seuil * FANTOME_MI_CHEMIN);
}
/** La lecture qui dit « cette run s'est éteinte ». */
export function estFantome(state) {
    return state.game_over === FANTOME;
}
/**
 * §9.4bis — télégraphie l'extinction, puis l'applique. MUTE `state`.
 *
 * **Le même chemin que les quatre autres**, pas un mécanisme parallèle : comme le burn-out
 * terminal (`resoudreCrise`) et les excuses ratées (`resoudreExcuses`), la fin s'inscrit dans
 * l'état au moment où elle se produit, et `verifierGameOver` la relit par le simple
 * `status === "game_over"`.
 *
 * **Télégraphiage en deux temps** (PLAN §4.11, « le jeu avertit avant de tuer ») : à mi-chemin
 * un `warning` tombe, puis **se répète à chaque semaine simulée** franchie jusqu'à la fin. La
 * répétition n'est pas du bruit, c'est la seule façon d'être honnête ici : les gardes de la
 * voie « extinction » (flops, momentum) peuvent s'armer **après** la mi-chemin, et un
 * avertissement unique posé trop tôt serait alors passé inaperçu. Le pas hebdomadaire se
 * déduit de l'horloge (`clockAvant`), donc **sans drapeau ajouté** — même technique que
 * `majFaillite`, qui n'émet ses alertes que sur le jalon franchi pendant CE pas.
 *
 * ✎ **Une victoire gravée éteint le Fantôme** : quand une rue porte ton nom, on ne t'oublie
 * plus. `victory_locked` garde donc la fonction en entier — la « mort en légende » reste
 * réservée aux quatre accidents.
 */
export function majFantome(state, clockAvant, silenceH, events = []) {
    if (state.victory_locked || state.status !== "active")
        return null;
    const delta = Math.max(0, state.clock_h - clockAvant);
    let atteinte = null;
    for (const v of voiesDuFantome(state, silenceH)) {
        if (!v.armee)
            continue;
        const miChemin = v.seuil * FANTOME_MI_CHEMIN;
        // ⟦A-79⟧ **« Jamais décollé » : un avertissement, puis le couperet — littéralement.**
        // Court et franc (« pas une agonie à étages »), et la GARANTIE que l'avertissement
        // précède la mort d'au moins une résolution : une crise de SM avance l'horloge de
        // 14 jours d'un coup, et sans ce témoin le silence pouvait franchir mi-chemin ET seuil
        // dans le même pas — un game over non télégraphié, ce que PLAN §4.11 interdit. Le
        // témoin se réarme dès que le joueur publie (le silence retombe sous mi-chemin).
        if (v.voie === "jamais_decolle") {
            if (v.heures < miChemin) {
                if (state.flags.fantome_prevenu)
                    state.flags.fantome_prevenu = false;
                continue;
            }
            if (state.flags.fantome_prevenu !== true) {
                state.flags.fantome_prevenu = true;
                events.push({
                    kind: "warning",
                    data: {
                        cause: "extinction_imminente",
                        voie: v.voie,
                        // ⟦A-79⟧ « tu n'as encore rien publié — MeTube oublie vite » ne se dit qu'à qui
                        // n'a VRAIMENT rien publié ; après une vraie vidéo, le silence a un autre texte.
                        jamais_publie: state.counters.videos_published === 0,
                        jours_ecoules: Math.floor(v.heures / H_JOUR),
                        jours_restants: Math.ceil((v.seuil - v.heures) / H_JOUR),
                    },
                });
                continue; // prévenu CE tour-ci : le couperet attend au moins la résolution suivante
            }
            if (v.heures >= v.seuil)
                atteinte ??= v.voie;
            continue;
        }
        // La voie « extinction » (milieu de partie) garde sa cadence d'⟦A-58⟧ — A-79 §2.3 la
        // laisse explicitement hors du recadrage.
        if (v.heures >= v.seuil) {
            // La première voie fermée gagne : `voiesDuFantome` les rend dans l'ordre de §9.4bis.
            atteinte ??= v.voie;
            continue;
        }
        if (v.heures < miChemin)
            continue;
        const avant = v.heures - delta;
        // Une alerte par semaine simulée franchie, jamais deux fois la même dans le même pas.
        const memeSemaine = avant >= miChemin && Math.floor(avant / H_SEMAINE) === Math.floor(v.heures / H_SEMAINE);
        if (memeSemaine)
            continue;
        events.push({
            kind: "warning",
            data: {
                cause: "extinction_imminente",
                voie: v.voie,
                jours_ecoules: Math.floor(v.heures / H_JOUR),
                jours_restants: Math.ceil((v.seuil - v.heures) / H_JOUR),
            },
        });
    }
    if (atteinte !== null)
        appliquerGameOver(state, FANTOME, events);
    return atteinte;
}
// ─────────────────────────────────────────────────────────────────────────────
// §11.2-11.5 Les 4 conditions, évaluées ensemble
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Évalue les game over qui se **lisent dans l'état** à tout instant (§11.4, §11.5) : le
 * bannissement et la faillite. Rend `null` si la run continue. **Ne mute rien.**
 *
 * Les trois autres fins sont des **événements**, pas des états : le burn-out terminal est
 * décidé par `resoudreCrise`, les excuses ratées pendant l'arc par `resoudreExcuses`, et
 * l'extinction §9.4bis par `majFantome`. Les trois inscrivent le game over quand il se
 * produit — c'est ce qui les rend détectables ici par le simple `status === "game_over"`.
 */
export function verifierGameOver(state, sys = SYSTEMES_TOUS_ACTIFS) {
    if (state.status === "game_over")
        return state.game_over;
    // 1. Burn-out terminal : décidé par `resoudreCrise` au moment de la crise (§11.2), qui
    //    inscrit le game over dans l'état — il est donc déjà attrapé par le test ci-dessus.
    //    Le re-dériver ici serait faux : le drapeau d'avertissement est réarmé après une
    //    crise survécue.
    // 3. Bannissement — 3 strikes actifs simultanément.
    // ⟦C60 · A-177⟧ Sans strikes, pas de bannissement.
    if (sys.strikes && strikesActifs(state).length >= STRIKES_FATAL)
        return "bannissement";
    // 4. Faillite — 480 h ininterrompues dans le rouge (impossible avant le Grand Saut, A-1).
    // ⟦C60 · A-177⟧ Sans faillite, pas de fin par faillite.
    if (sys.faillite &&
        state.flags.grand_saut_done &&
        state.money < 0 &&
        state.counters.money_negative_since_h !== null &&
        state.clock_h - state.counters.money_negative_since_h >= H_FAILLITE) {
        return "faillite";
    }
    // 2. Spirale de réputation : l'arc seul ne tue pas — seules des excuses ratées pendant
    //    l'arc le font (§11.2), et c'est `resoudreExcuses` qui le signale.
    return null;
}
/** L'unique écriture de `state.game_over` — sans conversion depuis ⟦A-74⟧ : l'union est complète. */
function ecrireGameOver(state, cause) {
    state.game_over = cause;
    state.status = "game_over";
}
/** Inscrit le game over dans l'état (§11.6). MUTE `state`. */
export function appliquerGameOver(state, cause, events = []) {
    // La victoire est gravée : rien ne peut la reprendre, même en hardcore (§9.4, PLAN §4.13).
    if (state.victory_locked) {
        events.push({
            kind: "game_over",
            data: { cause, mode: state.mode, legende: true, victoire_intacte: true },
        });
        ecrireGameOver(state, cause);
        return;
    }
    ecrireGameOver(state, cause);
    events.push({
        kind: "game_over",
        data: { cause, mode: state.mode, hardcore: state.mode === "hardcore" },
    });
}
// ─────────────────────────────────────────────────────────────────────────────
// §11.6 Reprise en mode normal
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Argent conservé à la reprise : 50 % de celui du snapshot, jamais négatif (§11.6).
 *
 * ⟦C60 bis · A-177⟧ **Ce n'est plus qu'un DÉFAUT** : la part que la chute garde est un réglage du
 * contenu (`meta.systemes.reprise_clicks`, `engine/menage.ts`), et `recomposerReprise` la reçoit en
 * paramètre. Cette constante est la valeur d'AVANT (l'état neutre ⟦A-10⟧), celle que `menage.ts`
 * redit en `REPRISE_CLICKS_DEFAUT` ; le contenu livré la porte à 1 : la chute ne retire plus rien.
 */
export const REPRISE_MONEY_RATIO = 0.5;
export const REPRISE_REPUTATION = 50;
export const REPRISE_MENTAL = 60;
/**
 * État de reprise en mode **normal** (§11.6) : le snapshot du dernier palier franchi,
 * **enrichi** de la progression conservée au moment de la mort (« skills conservés »,
 * PLAN §4.11), avec argent −50 %, REP 50, SM 60, énergie 100 et tous les compteurs de
 * menace remis à zéro.
 *
 * Pur : ne mute ni le snapshot ni l'état courant. La persistance et le choix du snapshot
 * relèvent du serveur (SPEC-API §7.3) — le moteur ne fait que dire à quoi la reprise
 * ressemble.
 */
export function recomposerReprise(snapshot, courant, 
/** ⟦C60 bis⟧ La part des Clicks du palier que la chute garde : le réglage du contenu, 0,5 avant. */
partDesClicks = REPRISE_MONEY_RATIO) {
    const repris = structuredClone(snapshot);
    // La progression survit à la mort (PLAN §4.11).
    repris.skills = [...courant.skills];
    repris.pc = courant.pc;
    repris.xp = courant.xp;
    repris.level = courant.level;
    // Le log de la run est linéaire : le turn ne revient jamais en arrière (SPEC-API §7.3).
    repris.turn = courant.turn;
    repris.money = Math.max(0, snapshot.money) * partDesClicks;
    repris.reputation = REPRISE_REPUTATION;
    repris.mental = REPRISE_MENTAL;
    repris.energy = 100;
    repris.status = "active";
    repris.game_over = null;
    // Compteurs de menace remis à zéro : on ne meurt pas deux fois de la même dette.
    repris.counters.sm_crisis_history_h = [];
    repris.counters.strikes = [];
    repris.counters.money_negative_since_h = null;
    repris.counters.consecutive_flops = 0;
    repris.flags.dernier_avertissement_burnout = false;
    repris.flags.arc_chute_active = false;
    repris.flags.sponsor_derniere_chance_used = false;
    repris.last_damage = null;
    repris.pending_event = null;
    return repris;
}
//# sourceMappingURL=gameover.js.map